import { ComponentType, lazy } from 'react'
import { Route } from 'react-router-dom'
import { chain } from 'lodash'
import { NotAuthorized } from 'components/Authentication'
import {
  CUSTOMERS_VIEW,
  INVENTORY_VIEW,
  REPORTS_VIEW,
  RESERVATIONS_VIEW,
  ROTAS_VIEW,
  SETTINGS_MANAGE,
  SETTINGS_VIEW,
  USERS_MANAGE,
} from 'constants/permissions'
import { useCurrentUserCan, useCurrentUserCanForCurrentSite } from 'hooks/UseCurrentUserCan'
import { useEffect } from 'react'
import { setPageTitle } from 'utilities/PageTitle'
import { componentLoader } from 'utilities/PreloadLinks'
// import { useActiveFeatureFlags } from 'contexts/SiteConfig'

const ReactLazyPreload = (importStatement: RouteInterface['importFrom']) => {
  const Component = lazy(() => componentLoader(importStatement))
  // @ts-expect-error
  Component.preload = importStatement
  return Component
}

const PrivateRoute = ({ component: Component, permissions = [], ...rest }) => {
  const can = useCurrentUserCan()

  useEffect(() => {
    if (rest.title) {
      setPageTitle(`${rest.title}`)
    } else {
      setPageTitle('Trybe')
    }
  }, [rest.title])

  return (
    <Route
      {...rest}
      render={(props) => {
        if (!chain(permissions).every((permission) => can(permission))) {
          return <NotAuthorized />
        }

        return <Component {...props} />
      }}
    />
  )
}

export const useCheckPermissions = () => {
  const can = useCurrentUserCanForCurrentSite()

  return (permissions: string[] | undefined) => permissions?.every((permission) => can(permission))
}

export interface RouteInterface {
  path: string
  name: string
  route: ComponentType<any>
  exact?: boolean
  permissions?: string[]
  importFrom: () => Promise<{ default: ComponentType<any> }>
}

// const FeatureFlagWrapper = ({ children, flag, active = true }) => {
//   const featureFlags = useActiveFeatureFlags()
//
//   if (active ? !featureFlags.includes(flag) : featureFlags.includes(flag)) {
//     return null
//   }
//
//   return children
// }

const routes: RouteInterface[] = [
  {
    path: '/notifications',
    name: 'Notifications',
    route: PrivateRoute,
    exact: true,
    importFrom: () =>
      import('./components/NotificationsList').then((file) => ({
        default: file.NotificationsList,
      })),
  },
  {
    path: '/offering-availability',
    name: 'Offering availability',
    route: PrivateRoute,
    exact: true,
    permissions: [SETTINGS_MANAGE],
    importFrom: () => import('./screens/OfferingAvailabilityCalendar'),
  },
  {
    path: '/practitioner-availability',
    name: 'Practitioner availability',
    route: PrivateRoute,
    exact: true,
    permissions: [ROTAS_VIEW],
    importFrom: () => import('./screens/PractitionerAvailabilityCalendar'),
  },
  {
    path: '/calendar',
    name: 'Calendar',
    route: PrivateRoute,
    exact: true,
    permissions: [RESERVATIONS_VIEW],
    importFrom: () => import('./components/Calendar'),
  },
  {
    path: '/calendar/schedule',
    name: 'Schedule',
    route: PrivateRoute,
    exact: true,
    permissions: [RESERVATIONS_VIEW],
    importFrom: () => import('./components/Calendar'),
  },
  {
    path: '/reports',
    name: 'Reports',
    route: PrivateRoute,
    permissions: [REPORTS_VIEW],
    importFrom: () => import('./screens/Reports'),
  },
  {
    path: '/',
    exact: true,
    name: 'Home',
    route: PrivateRoute,
    permissions: [REPORTS_VIEW],
    importFrom: () => import('./screens/Dashboards/Home'),
  },
  {
    path: '/dashboards',
    name: 'Home',
    route: PrivateRoute,
    permissions: [REPORTS_VIEW],
    importFrom: () => import('./screens/Dashboards'),
  },
  {
    path: '/admin/feature-flags',
    name: 'Feature flags',
    route: PrivateRoute,
    importFrom: () =>
      import('./screens/FeatureFlags').then((file) => ({ default: file.FeatureFlags })),
  },
  {
    path: '/admin/sites',
    name: 'Sites',
    exact: true,
    route: PrivateRoute,
    importFrom: () =>
      import('./screens/ManageSites').then((file) => ({ default: file.ManageSites })),
  },
  {
    path: '/admin/sites/:siteId',
    name: 'Sites',
    exact: true,
    route: PrivateRoute,
    importFrom: () =>
      import('./screens/ManageSites/Details').then((file) => ({ default: file.SiteDetail })),
  },
  {
    path: '/admin/sites/:siteId/adyen-setup',
    name: 'Sites',
    route: PrivateRoute,
    importFrom: () => import('./screens/AdyenSetup').then((file) => ({ default: file.AdyenSetup })),
  },
  {
    path: '/admin/users',
    name: 'Users',
    exact: true,
    route: PrivateRoute,
    importFrom: () =>
      import('./screens/ManageUsers').then((file) => ({ default: file.ManageUsers })),
  },
  {
    path: '/admin/users/:userId',
    name: 'Users',
    exact: true,
    route: PrivateRoute,
    importFrom: () =>
      import('./screens/ManageUsers/Details').then((file) => ({ default: file.UserDetail })),
  },
  {
    path: '/admin/deposit-exposure',
    name: 'Sites',
    exact: true,
    route: PrivateRoute,
    importFrom: () =>
      import('./screens/DepositExposure').then((file) => ({ default: file.DepositExposure })),
  },
  {
    path: '/settings/inventory/categories',
    name: 'Stock categories',
    route: PrivateRoute,
    permissions: [SETTINGS_VIEW],
    importFrom: () =>
      import('./components/SettingsDirectory/InventoryCategories').then((file) => ({
        default: file.InventoryCategories,
      })),
  },
  {
    path: '/settings/inventory/brands',
    name: 'Stock brands',
    route: PrivateRoute,
    permissions: [SETTINGS_VIEW],
    importFrom: () =>
      import('./components/SettingsDirectory/InventoryBrands').then((file) => ({
        default: file.InventoryBrands,
      })),
  },
  {
    path: '/settings/inventory/suppliers',
    name: 'Suppliers',
    route: PrivateRoute,
    exact: true,
    permissions: [SETTINGS_VIEW],
    importFrom: () =>
      import('./components/SettingsDirectory/InventorySuppliers').then((file) => ({
        default: file.InventorySuppliers,
      })),
  },
  {
    path: '/settings/inventory/suppliers/:supplierId',
    name: 'Supplier settings',
    route: PrivateRoute,
    permissions: [SETTINGS_VIEW],
    importFrom: () =>
      import('./components/SettingsDirectory/InventorySuppliers/Edit').then((file) => ({
        default: file.EditInventorySupplierSettings,
      })),
  },
  {
    path: '/settings/resources/equipment/:equipmentId',
    name: 'Equipment settings',
    route: PrivateRoute,
    permissions: [SETTINGS_VIEW],
    importFrom: () =>
      import('./components/SettingsDirectory/Resources/Equipment/EditEquipmentSettings').then(
        (file) => ({ default: file.EditEquipmentSettings })
      ),
  },
  {
    path: '/settings/resources/rooms/:roomId',
    name: 'Room settings',
    route: PrivateRoute,
    permissions: [SETTINGS_VIEW],
    importFrom: () =>
      import('./components/SettingsDirectory/Resources/Rooms/EditRoomSettings').then((file) => ({
        default: file.EditRoomSettings,
      })),
  },
  {
    path: '/bookable-areas/:bookableAreaId/*',
    name: 'Bookable Area Settings',
    route: PrivateRoute,
    exact: true,
    permissions: [SETTINGS_VIEW],
    importFrom: () => import('./components/SettingsDirectory/Resources/BookableAreas/Edit'),
  },
  {
    path: '/settings/resources/practitioners/:practitionerId',
    name: 'Practitioner settings',
    route: PrivateRoute,
    permissions: [SETTINGS_VIEW],
    importFrom: () =>
      import('./components/SettingsDirectory/Resources/Practitioners/Edit').then((file) => ({
        default: file.EditPractitionerSettings,
      })),
  },
  {
    path: '/settings/offerings/package-types/:packageTypeId',
    name: 'Package type settings',
    route: PrivateRoute,
    permissions: [SETTINGS_VIEW],
    importFrom: () =>
      import('./components/SettingsDirectory/Offerings/Packages/Edit').then((file) => ({
        default: file.EditPackageSettings,
      })),
  },
  {
    path: '/settings/offerings/course-types/:courseTypeId',
    name: 'Course type settings',
    route: PrivateRoute,
    permissions: [SETTINGS_VIEW],
    importFrom: () =>
      import('./components/SettingsDirectory/Offerings/Courses/Edit').then((file) => ({
        default: file.EditCourseTypeSettings,
      })),
  },
  {
    path: '/settings/offerings/session-types/:sessionTypeId',
    name: 'Session type settings',
    route: PrivateRoute,
    permissions: [SETTINGS_VIEW],
    importFrom: () =>
      import('./components/SettingsDirectory/Offerings/SessionTypes/Edit').then((file) => ({
        default: file.EditSessionTypeSettings,
      })),
  },
  {
    path: '/settings/offerings/appointment-types/:appointmentTypeId',
    name: 'Appointment type settings',
    route: PrivateRoute,
    permissions: [SETTINGS_VIEW],
    importFrom: () =>
      import('./components/SettingsDirectory/Offerings/AppointmentTypes/Edit').then((file) => ({
        default: file.EditAppointmentTypeSettings,
      })),
  },
  {
    path: '/settings/offerings/area-booking-types/:areaBookingTypeId',
    name: 'Booking type settings',
    route: PrivateRoute,
    permissions: [SETTINGS_VIEW],
    importFrom: () =>
      import('./components/SettingsDirectory/Offerings/AreaBookingTypes/Edit').then((file) => ({
        default: file.EditAreaBookingTypeSettings,
      })),
  },
  {
    path: '/table-reservation-types/:reservationTypeId/*',
    name: 'Table reservation type settings',
    route: PrivateRoute,
    exact: true,
    permissions: [SETTINGS_VIEW],
    importFrom: () => import('./components/SettingsDirectory/Offerings/TableReservationTypes/Edit'),
  },
  {
    path: '/my-account',
    name: 'My Account',
    route: PrivateRoute,
    importFrom: () => import('./screens/MyAccount'),
  },
  {
    path: '/settings/offerings/retail-products/:retailProductId',
    name: 'Retail product settings',
    route: PrivateRoute,
    permissions: [SETTINGS_VIEW],
    importFrom: () =>
      import('./components/SettingsDirectory/Offerings/RetailProducts/Edit').then((file) => ({
        default: file.EditRetailProductSettings,
      })),
  },
  {
    path: '/settings/offerings/voucher-types/:voucherTypeId',
    name: 'Voucher type settings',
    route: PrivateRoute,
    permissions: [SETTINGS_VIEW],
    importFrom: () =>
      import('./components/SettingsDirectory/Offerings/VoucherTypes/Edit').then((file) => ({
        default: file.EditVoucherTypeSettings,
      })),
  },
  {
    path: '/settings/external-vouchers',
    name: 'Vouchers integration',
    route: PrivateRoute,
    exact: true,
    permissions: [SETTINGS_VIEW],
    importFrom: () =>
      import('./components/SettingsDirectory/ExternalVouchers').then((file) => ({
        default: file.Vouchers,
      })),
  },
  {
    path: '/settings/clients/intake-forms',
    name: 'Intake forms',
    route: PrivateRoute,
    exact: true,
    permissions: [SETTINGS_VIEW],
    importFrom: () =>
      import('./components/SettingsDirectory/Clients/IntakeFormBuilder').then((file) => ({
        default: file.IntakeFormBuilder,
      })),
  },
  {
    path: '/settings/clients/intake-forms/:sectionId',
    name: 'Edit intake form section',
    route: PrivateRoute,
    exact: true,
    permissions: [SETTINGS_VIEW],
    importFrom: () =>
      import('./components/SettingsDirectory/Clients/IntakeFormSectionBuilder').then((file) => ({
        default: file.IntakeFormSectionBuilder,
      })),
  },
  {
    path: '/settings/clients/intake-forms/:sectionId/questions/:questionId',
    name: 'Edit intake form question',
    route: PrivateRoute,
    exact: true,
    permissions: [SETTINGS_VIEW],
    importFrom: () =>
      import('./components/SettingsDirectory/Clients/IntakeFormQuestionBuilder').then((file) => ({
        default: file.IntakeFormQuestionBuilder,
      })),
  },
  {
    path: '/external-vouchers/:voucherId/settings',
    name: 'Vouchers integration settings',
    route: PrivateRoute,
    exact: true,
    permissions: [SETTINGS_VIEW],
    importFrom: () => import('./screens/EditExternalVoucherSettings'),
  },
  {
    path: '/settings/orders/discount-types-and-promo-codes/:discountTypeId',
    name: 'Discount type settings',
    route: PrivateRoute,
    permissions: [SETTINGS_VIEW],
    importFrom: () =>
      import('./components/SettingsDirectory/Orders/DiscountTypesAndPromoCodes/Edit').then(
        (file) => ({ default: file.EditTypeOrCodeSettings })
      ),
  },
  {
    path: '/orders',
    name: 'Orders',
    route: PrivateRoute,
    exact: true,
    permissions: [RESERVATIONS_VIEW],
    importFrom: () => import('./screens/OrderList'),
  },
  {
    path: '/orders/enquiries',
    name: 'Enquiries',
    route: PrivateRoute,
    exact: true,
    permissions: [RESERVATIONS_VIEW],
    importFrom: () => import('./screens/EnquiryList/EnquiryList'),
  },
  {
    path: '/beta/orders',
    name: 'Orders',
    route: PrivateRoute,
    exact: true,
    permissions: [RESERVATIONS_VIEW],
    importFrom: () => import('./screens/OrderList/OrderListNew'),
  },
  {
    path: '/orders/:orderId?',
    name: 'Order details',
    route: PrivateRoute,
    permissions: [RESERVATIONS_VIEW],
    importFrom: () => import('./screens/OrderDetails'),
  },
  {
    path: '/beta/orders/:orderId?',
    name: 'Order details',
    route: PrivateRoute,
    permissions: [RESERVATIONS_VIEW],
    importFrom: () => import('./screens/OrderDetailsNew/OrderDetails'),
  },
  {
    path: '/visits',
    name: 'Visits',
    exact: true,
    route: PrivateRoute,
    permissions: [RESERVATIONS_VIEW],
    importFrom: () => import('./screens/Visits/List'),
  },
  {
    path: '/visits/:visitId',
    name: 'Visit details',
    exact: true,
    route: PrivateRoute,
    permissions: [RESERVATIONS_VIEW],
    importFrom: () => import('./screens/Visits/Details'),
  },
  {
    path: '/visits/:visitId/messages',
    name: 'Visit details',
    exact: true,
    route: PrivateRoute,
    permissions: [RESERVATIONS_VIEW],
    importFrom: () => import('./screens/Visits/Details'),
  },
  {
    path: '/visits/:visitId/audit',
    name: 'Visit details',
    exact: true,
    route: PrivateRoute,
    permissions: [RESERVATIONS_VIEW],
    importFrom: () => import('./screens/Visits/Details'),
  },
  {
    path: '/inventory/stock-takes/:stockTakeId',
    name: 'Stocktake',
    route: PrivateRoute,
    exact: true,
    permissions: [INVENTORY_VIEW],
    importFrom: () =>
      import('./screens/InventoryV2/StockTakes/Detail').then((file) => ({
        default: file.StockTakeDetail,
      })),
  },
  {
    path: '/inventory/stock-items',
    name: 'Stock items',
    route: PrivateRoute,
    exact: true,
    permissions: [INVENTORY_VIEW],
    importFrom: () =>
      import('./screens/InventoryV2/Stock').then((file) => ({
        default: file.Stock,
      })),
  },
  {
    path: '/inventory/stock-items/:productId',
    name: 'Stock item details',
    route: PrivateRoute,
    permissions: [INVENTORY_VIEW],
    importFrom: () =>
      import('./screens/InventoryV2/Stock/StockDetails').then((file) => ({
        default: file.EditStockDetails,
      })),
  },
  {
    path: '/inventory/stock-orders',
    name: 'Stock orders',
    route: PrivateRoute,
    exact: true,
    permissions: [INVENTORY_VIEW],
    importFrom: () =>
      import('./screens/InventoryV2/StockOrders').then((file) => ({
        default: file.StockOrders,
      })),
  },
  {
    path: '/inventory/stock-orders/:orderId',
    name: 'Stock order',
    route: PrivateRoute,
    exact: true,
    permissions: [INVENTORY_VIEW],
    importFrom: () =>
      import('./screens/InventoryV2/StockOrders/StockOrderDetails').then((file) => ({
        default: file.StockOrderDetails,
      })),
  },
  {
    path: '/inventory/stock-takes',
    name: 'Stocktakes',
    route: PrivateRoute,
    exact: true,
    permissions: [INVENTORY_VIEW],
    importFrom: () =>
      import('./screens/InventoryV2/StockTakes').then((file) => ({
        default: file.StockTakes,
      })),
  },
  {
    path: '/vouchers',
    name: 'Vouchers',
    route: PrivateRoute,
    exact: true,
    permissions: [RESERVATIONS_VIEW],
    importFrom: () =>
      import('./screens/VoucherCodesList/VoucherCodesList').then((file) => ({
        default: file.VoucherCodesList,
      })),
  },
  {
    path: '/vouchers/:voucherCodeId',
    name: 'Voucher code',
    route: PrivateRoute,
    exact: true,
    permissions: [RESERVATIONS_VIEW],
    importFrom: () =>
      import('./screens/VoucherCodeDetails/VoucherCodeDetails').then((file) => ({
        default: file.VoucherCodeDetails,
      })),
  },
  {
    path: '/settings/billing',
    name: 'Billing',
    route: PrivateRoute,
    exact: true,
    importFrom: () =>
      import('./components/SettingsDirectory/System/Billing').then((file) => ({
        default: file.Billing,
      })),
  },
  {
    path: '/audit-logs',
    name: 'Audit logs',
    route: PrivateRoute,
    exact: true,
    importFrom: () =>
      import('./screens/AuditLog').then((file) => ({
        default: file.AuditLog,
      })),
  },
  {
    path: '/settings/data-import',
    name: 'Importer',
    exact: true,
    route: PrivateRoute,
    importFrom: () =>
      import('./components/SettingsDirectory/System/Importer').then((file) => ({
        default: file.Importer,
      })),
  },
  {
    path: '/settings/data-import/:importId',
    name: 'Importer',
    route: PrivateRoute,
    importFrom: () =>
      import('./components/SettingsDirectory/System/Importer/Detail').then((file) => ({
        default: file.ImporterDetail,
      })),
  },
  {
    path: '/settings/clients/membership',
    name: 'Membership settings',
    route: PrivateRoute,
    exact: true,
    importFrom: () =>
      import('./components/SettingsDirectory/Clients/Membership').then((file) => ({
        default: file.Membership,
      })),
  },
  {
    path: '/settings/email-branding',
    name: 'Email branding',
    route: PrivateRoute,
    exact: true,
    permissions: [SETTINGS_VIEW],
    importFrom: () =>
      import('./components/SettingsDirectory/Marketing/EmailBranding').then((file) => ({
        default: file.EmailBranding,
      })),
  },
  {
    path: '/settings/resources/bookable-areas',
    name: 'Bookable areas',
    route: PrivateRoute,
    exact: true,
    permissions: [SETTINGS_VIEW],
    importFrom: () =>
      import('./components/SettingsDirectory/Resources/BookableAreas').then((file) => ({
        default: file.BookableAreas,
      })),
  },
  {
    path: '/settings/offerings/area-booking-types',
    name: 'Area booking types',
    route: PrivateRoute,
    exact: true,
    permissions: [SETTINGS_VIEW],
    importFrom: () =>
      import('./components/SettingsDirectory/Offerings/AreaBookingTypes').then((file) => ({
        default: file.AreaBookingTypes,
      })),
  },
  {
    path: '/settings/offerings/retail-products',
    name: 'Retail products',
    route: PrivateRoute,
    exact: true,
    permissions: [SETTINGS_VIEW],
    importFrom: () =>
      import('./components/SettingsDirectory/Offerings/RetailProducts').then((file) => ({
        default: file.RetailProducts,
      })),
  },
  {
    path: '/settings/offerings/appointment-types',
    name: 'Appointment types',
    route: PrivateRoute,
    exact: true,
    permissions: [SETTINGS_VIEW],
    importFrom: () =>
      import('./components/SettingsDirectory/Offerings/AppointmentTypes').then((file) => ({
        default: file.AppointmentTypes,
      })),
  },
  {
    path: '/settings/offerings/session-types',
    name: 'Session types',
    route: PrivateRoute,
    exact: true,
    permissions: [SETTINGS_VIEW],
    importFrom: () =>
      import('./components/SettingsDirectory/Offerings/SessionTypes').then((file) => ({
        default: file.SessionTypes,
      })),
  },
  {
    path: '/settings/offerings/package-types',
    name: 'Package types',
    route: PrivateRoute,
    exact: true,
    permissions: [SETTINGS_VIEW],
    importFrom: () =>
      import('./components/SettingsDirectory/Offerings/Packages').then((file) => ({
        default: file.Packages,
      })),
  },
  {
    path: '/settings/offerings/courses',
    name: 'Courses',
    route: PrivateRoute,
    exact: true,
    permissions: [SETTINGS_VIEW],
    importFrom: () =>
      import('./components/SettingsDirectory/Offerings/Courses').then((file) => ({
        default: file.Courses,
      })),
  },
  {
    path: '/settings/offerings/vouchers',
    name: 'Vouchers',
    route: PrivateRoute,
    exact: true,
    permissions: [SETTINGS_VIEW],
    importFrom: () =>
      import('./components/SettingsDirectory/Offerings/VoucherTypes').then((file) => ({
        default: file.VoucherTypes,
      })),
  },
  {
    path: '/settings/orders/sales-channels',
    name: 'Sales channels',
    route: PrivateRoute,
    exact: true,
    permissions: [SETTINGS_VIEW],
    importFrom: () =>
      import('./components/SettingsDirectory/Orders/SalesChannels').then((file) => ({
        default: file.SalesChannels,
      })),
  },
  {
    path: '/settings/orders/discount-types-and-promo-codes',
    name: 'Discount types and promo codes',
    route: PrivateRoute,
    exact: true,
    permissions: [SETTINGS_VIEW],
    importFrom: () =>
      import('./components/SettingsDirectory/Orders/DiscountTypesAndPromoCodes').then((file) => ({
        default: file.DiscountTypesAndPromoCodes,
      })),
  },
  {
    path: '/settings/orders/order-labels',
    name: 'Order labels',
    route: PrivateRoute,
    exact: true,
    permissions: [SETTINGS_VIEW],
    importFrom: () =>
      import('./components/SettingsDirectory/Orders/OrderLabels').then((file) => ({
        default: file.OrderLabels,
      })),
  },
  {
    path: '/settings/orders/external-ids',
    name: 'External IDs',
    route: PrivateRoute,
    exact: true,
    permissions: [SETTINGS_VIEW],
    importFrom: () =>
      import('./components/SettingsDirectory/Orders/ExternalIds').then((file) => ({
        default: file.ExternalIds,
      })),
  },
  {
    path: '/settings/orders/auto-checkout',
    name: 'Auto checkout',
    route: PrivateRoute,
    exact: true,
    permissions: [SETTINGS_VIEW],
    importFrom: () =>
      import('./components/SettingsDirectory/Orders/AutoCheckout').then((file) => ({
        default: file.AutoCheckout,
      })),
  },
  {
    path: '/settings/table-reservation-types',
    name: 'Table reservation types',
    route: PrivateRoute,
    exact: true,
    permissions: [SETTINGS_VIEW],
    importFrom: () => import('./components/SettingsDirectory/Offerings/TableReservationTypes'),
  },
  {
    path: '/settings/orders/service-charge',
    name: 'Service charge and tips',
    route: PrivateRoute,
    exact: true,
    permissions: [SETTINGS_VIEW],
    importFrom: () =>
      import('./components/SettingsDirectory/Orders/ServiceCharge').then((file) => ({
        default: file.ServiceCharge,
      })),
  },
  {
    path: '/settings/shop',
    name: 'Shop settings',
    route: PrivateRoute,
    exact: true,
    importFrom: () =>
      import('./components/SettingsDirectory/Marketing/Shop').then((file) => ({
        default: file.ShopSettings,
      })),
  },
  {
    path: '/settings/shop/*',
    name: 'Shop settings',
    route: PrivateRoute,
    exact: true,
    importFrom: () =>
      import('./components/SettingsDirectory/Marketing/Shop').then((file) => ({
        default: file.ShopSettings,
      })),
  },
  {
    path: '/settings/visit-types',
    name: 'Visit types',
    route: PrivateRoute,
    exact: true,
    importFrom: () =>
      import('./components/SettingsDirectory/Marketing/VisitTypes').then((file) => ({
        default: file.VisitTypes,
      })),
  },
  {
    path: '/settings/comms-locales',
    name: 'Communication languages',
    route: PrivateRoute,
    exact: true,
    importFrom: () =>
      import('./components/SettingsDirectory/Marketing/CommsLocales').then((file) => ({
        default: file.CommsLocales,
      })),
  },
  {
    path: '/settings/visit-types/:visitTypeId',
    name: 'Visit type settings',
    route: PrivateRoute,
    exact: true,
    importFrom: () =>
      import('./components/SettingsDirectory/Marketing/VisitTypes/Details').then((file) => ({
        default: file.VisitTypeDetails,
      })),
  },
  {
    path: '/settings',
    name: 'Settings',
    route: PrivateRoute,
    exact: true,
    permissions: [INVENTORY_VIEW],
    importFrom: () => import('./components/SettingsDirectory'),
  },
  {
    path: '/clients/:clientId',
    name: 'Customer details',
    route: PrivateRoute,
    importFrom: () => import('./components/CustomerDirectory/ClientDetail'),
  },
  {
    path: '/clients',
    name: 'Clients',
    route: PrivateRoute,
    permissions: [CUSTOMERS_VIEW],
    importFrom: () =>
      import('./components/ClientList').then((file) => ({ default: file.ClientList })),
  },
  {
    path: '/memberships/:membershipId',
    name: 'Membership',
    route: PrivateRoute,
    permissions: [CUSTOMERS_VIEW],
    importFrom: () =>
      import('./components/MembershipDetails').then((file) => ({
        default: file.MembershipDetails,
      })),
  },
  {
    path: '/memberships',
    name: 'Memberships',
    route: PrivateRoute,
    permissions: [CUSTOMERS_VIEW],
    importFrom: () =>
      import('./components/MembershipList').then((file) => ({ default: file.MembershipList })),
  },
  {
    path: '/settings/legal',
    name: 'Legal settings',
    route: PrivateRoute,
    importFrom: () =>
      import('./components/SettingsDirectory/Marketing/Legal').then((file) => ({
        default: file.Legal,
      })),
  },
  {
    path: '/settings/site/opening-hours',
    name: 'Opening hours',
    route: PrivateRoute,
    importFrom: () =>
      import('./components/SettingsDirectory/Site/OpeningHours').then((file) => ({
        default: file.OpeningHours,
      })),
  },
  {
    path: '/settings/site/payment-types',
    name: 'Payment types',
    route: PrivateRoute,
    importFrom: () =>
      import('./components/SettingsDirectory/Site/PaymentTypes').then((file) => ({
        default: file.PaymentTypes,
      })),
  },
  {
    path: '/settings/site/appointment-enquiries',
    name: 'Appointment enquiries',
    route: PrivateRoute,
    exact: true,
    permissions: [SETTINGS_VIEW],
    importFrom: () =>
      import('./components/SettingsDirectory/Site/AppointmentEnquiries').then((file) => ({
        default: file.AppointmentEnquiries,
      })),
  },
  {
    path: '/settings/organisation/revenue-centres',
    name: 'Revenue centres',
    route: PrivateRoute,
    importFrom: () =>
      import('./components/SettingsDirectory/Organisation/RevenueCentres').then((file) => ({
        default: file.RevenueCentres,
      })),
  },
  {
    path: '/settings/integrations',
    name: 'Integrations',
    route: PrivateRoute,
    exact: true,
    importFrom: () =>
      import('./components/SettingsDirectory/Site/Integrations').then((file) => ({
        default: file.Integrations,
      })),
  },
  {
    path: '/settings/integrations/avvio',
    name: 'Avvio settings',
    route: PrivateRoute,
    importFrom: () =>
      import('./components/SettingsDirectory/Site/Integrations/Avvio').then((file) => ({
        default: file.Avvio,
      })),
  },
  {
    path: '/settings/integrations/resdiary',
    name: 'ResDiary integrations',
    route: PrivateRoute,
    exact: true,
    importFrom: () =>
      import('./components/SettingsDirectory/Site/Integrations/ResDiary').then((file) => ({
        default: file.ResDiary,
      })),
  },
  {
    path: '/settings/integrations/resdiary/:configId',
    name: 'ResDiary settings',
    route: PrivateRoute,
    importFrom: () =>
      import('./components/SettingsDirectory/Site/Integrations/ResDiary/Settings').then((file) => ({
        default: file.Settings,
      })),
  },
  {
    path: '/settings/integrations/fiskaltrust',
    name: 'Fiskaltrust settings',
    route: PrivateRoute,
    importFrom: () =>
      import('./components/SettingsDirectory/Site/Integrations/Fiskaltrust').then((file) => ({
        default: file.Fiskaltrust,
      })),
  },
  {
    path: '/settings/integrations/giftpro',
    name: 'GiftPro settings',
    route: PrivateRoute,
    importFrom: () =>
      import('./components/SettingsDirectory/Site/Integrations/GiftPro').then((file) => ({
        default: file.GiftPro,
      })),
  },
  {
    path: '/settings/integrations/for-sight',
    name: 'ForSight settings',
    route: PrivateRoute,
    importFrom: () =>
      import('./components/SettingsDirectory/Site/Integrations/ForSight').then((file) => ({
        default: file.ForSight,
      })),
  },
  {
    path: '/settings/integrations/belmond-bapi',
    name: 'BAPI settings',
    route: PrivateRoute,
    importFrom: () =>
      import('./components/SettingsDirectory/Site/Integrations/BelmondBapi').then((file) => ({
        default: file.BelmondBapi,
      })),
  },
  {
    path: '/settings/integrations/hconnect',
    name: 'HConnect settings',
    route: PrivateRoute,
    importFrom: () =>
      import('./components/SettingsDirectory/Site/Integrations/HConnect').then((file) => ({
        default: file.HConnect,
      })),
  },
  {
    path: '/settings/integrations/guestline',
    name: 'Guestline settings',
    route: PrivateRoute,
    importFrom: () =>
      import('./components/SettingsDirectory/Site/Integrations/Guestline').then((file) => ({
        default: file.Guestline,
      })),
  },
  {
    path: '/settings/integrations/hotsoft',
    name: 'HotSoft settings',
    route: PrivateRoute,
    importFrom: () =>
      import('./components/SettingsDirectory/Site/Integrations/HotSoft').then((file) => ({
        default: file.HotSoft,
      })),
  },
  {
    path: '/settings/integrations/maestro-pos',
    name: 'Maestro settings',
    route: PrivateRoute,
    importFrom: () =>
      import('./components/SettingsDirectory/Site/Integrations/MaestroPos').then((file) => ({
        default: file.MaestroPos,
      })),
  },
  {
    path: '/settings/integrations/mews',
    name: 'Mews settings',
    route: PrivateRoute,
    importFrom: () =>
      import('./components/SettingsDirectory/Site/Integrations/Mews').then((file) => ({
        default: file.Mews,
      })),
  },
  {
    path: '/settings/integrations/opera-fias',
    name: 'Opera FIAS settings',
    route: PrivateRoute,
    importFrom: () =>
      import('./components/SettingsDirectory/Site/Integrations/OperaFias').then((file) => ({
        default: file.OperaFias,
      })),
  },
  {
    path: '/settings/integrations/ohip',
    name: 'OHIP settings',
    route: PrivateRoute,
    importFrom: () =>
      import('./components/SettingsDirectory/Site/Integrations/Ohip').then((file) => ({
        default: file.Ohip,
      })),
  },
  {
    path: '/settings/integrations/stayntouch',
    name: 'Stayntouch settings',
    route: PrivateRoute,
    importFrom: () =>
      import('./components/SettingsDirectory/Site/Integrations/Stayntouch').then((file) => ({
        default: file.Stayntouch,
      })),
  },
  {
    path: '/settings/integrations/google-analytics',
    name: 'Google Analytics settings',
    route: PrivateRoute,
    importFrom: () =>
      import('./components/SettingsDirectory/Site/Integrations/GoogleAnalytics').then((file) => ({
        default: file.GoogleAnalytics,
      })),
  },
  {
    path: '/settings/integrations/google-tag-manager',
    name: 'Google Tag Manager settings',
    route: PrivateRoute,
    importFrom: () =>
      import('./components/SettingsDirectory/Site/Integrations/GoogleTagManager').then((file) => ({
        default: file.GoogleTagManager,
      })),
  },
  {
    path: '/settings/integrations/gantner',
    name: 'Gantner settings',
    route: PrivateRoute,
    exact: true,
    importFrom: () =>
      import('./components/SettingsDirectory/Site/Integrations/Gantner').then((file) => ({
        default: file.Gantner,
      })),
  },
  {
    path: '/settings/integrations/gantner/devices/:deviceId/entry-rule/:entryRuleId',
    name: 'Edit entry rule',
    route: PrivateRoute,
    exact: true,
    importFrom: () =>
      import(
        './components/SettingsDirectory/Site/Integrations/Gantner/EditDevice/EntryRulePage'
      ).then((file) => ({ default: file.EntryRulePage })),
  },
  {
    path: '/settings/integrations/gantner/devices/:deviceId/entry-rule',
    name: 'New entry rule',
    route: PrivateRoute,
    exact: true,
    importFrom: () =>
      import(
        './components/SettingsDirectory/Site/Integrations/Gantner/EditDevice/EntryRulePage'
      ).then((file) => ({ default: file.EntryRulePage })),
  },
  {
    path: '/settings/integrations/gantner/devices/:deviceId',
    name: 'Gantner device settings',
    route: PrivateRoute,
    exact: true,
    importFrom: () =>
      import('./components/SettingsDirectory/Site/Integrations/Gantner/EditDevice').then(
        (file) => ({ default: file.GantnerEditDevice })
      ),
  },
  {
    path: '/settings/integrations/mobile-passes',
    name: 'Mobile passes settings',
    route: PrivateRoute,
    importFrom: () =>
      import('./components/SettingsDirectory/Clients/MobilePasses').then((file) => ({
        default: file.MobilePasses,
      })),
  },
  {
    path: '/settings/integrations/sso',
    name: 'Single Sign-on',
    route: PrivateRoute,
    importFrom: () =>
      import('./components/SettingsDirectory/Site/Integrations/SingleSignOn').then((file) => ({
        default: file.SingleSignOn,
      })),
  },
  {
    path: '/settings/integrations/payments',
    name: 'Payments',
    route: PrivateRoute,
    importFrom: () =>
      import('./components/SettingsDirectory/Site/Integrations/Payments').then((file) => ({
        default: file.Payments,
      })),
  },
  {
    path: '/settings/integrations/kiosk/instructions/:kioskInstructionId',
    name: 'Edit kiosk instruction',
    route: PrivateRoute,
    exact: true,
    importFrom: () =>
      import(
        './components/SettingsDirectory/Site/Integrations/Kiosk/KioskInstructions/EditInstruction'
      ).then((file) => ({ default: file.EditKioskInstruction })),
  },
  {
    path: '/settings/integrations/kiosk/simulator',
    name: 'Kiosk simulator',
    route: PrivateRoute,
    importFrom: () =>
      import('./components/SettingsDirectory/Site/Integrations/Kiosk/Simulator').then((file) => ({
        default: file.KioskSimulator,
      })),
  },
  {
    path: '/settings/integrations/kiosk',
    name: 'Kiosk settings',
    route: PrivateRoute,
    importFrom: () =>
      import('./components/SettingsDirectory/Site/Integrations/Kiosk/Edit').then((file) => ({
        default: file.EditKioskSettings,
      })),
  },
  {
    path: '/settings/integrations/escpos',
    name: 'Receipt printing',
    route: PrivateRoute,
    importFrom: () =>
      import('./components/SettingsDirectory/Site/Integrations/EscPosConnectors').then((file) => ({
        default: file.EscPosConnectors,
      })),
  },
  {
    path: '/settings/integrations/webhooks',
    name: 'Webhooks',
    exact: true,
    route: PrivateRoute,
    importFrom: () =>
      import('./components/SettingsDirectory/Site/Integrations/Webhooks').then((file) => ({
        default: file.Webhooks,
      })),
  },
  {
    path: '/settings/integrations/webhooks/:configId',
    name: 'Webhook config',
    route: PrivateRoute,
    importFrom: () =>
      import('./components/SettingsDirectory/Site/Integrations/Webhooks/Settings').then((file) => ({
        default: file.Settings,
      })),
  },
  {
    path: '/settings/integrations/rms',
    name: 'RMS',
    exact: true,
    route: PrivateRoute,
    importFrom: () =>
      import('./components/SettingsDirectory/Site/Integrations/Rms').then((file) => ({
        default: file.Rms,
      })),
  },
  {
    path: '/settings/integrations/tei',
    name: 'TEI Integration',
    exact: true,
    route: PrivateRoute,
    importFrom: () =>
      import('./components/SettingsDirectory/Site/Integrations/Tei').then((file) => ({
        default: file.TeiIntegration,
      })),
  },
  {
    path: '/settings/integrations/vips',
    name: 'VIPS',
    exact: true,
    route: PrivateRoute,
    importFrom: () =>
      import('./components/SettingsDirectory/Site/Integrations/Vips').then((file) => ({
        default: file.Vips,
      })),
  },
  {
    path: '/settings/ota-availability',
    name: 'OTA Availability',
    route: PrivateRoute,
    exact: true,
    importFrom: () =>
      import('./components/SettingsDirectory/Site/OtaAvailability').then((file) => ({
        default: file.OtaAvailability,
      })),
  },
  {
    path: '/settings/resources/blocked-time-presets',
    name: 'Blocked time presets',
    route: PrivateRoute,
    importFrom: () =>
      import('./components/SettingsDirectory/Resources/BlockedTimePresets').then((file) => ({
        default: file.BlockedTimePresets,
      })),
  },
  {
    path: '/settings/site/zones',
    name: 'Zones',
    route: PrivateRoute,
    importFrom: () =>
      import('./components/SettingsDirectory/Site/Zones').then((file) => ({ default: file.Zones })),
  },
  {
    path: '/settings/resources/equipment',
    name: 'Equipment',
    route: PrivateRoute,
    importFrom: () =>
      import('./components/SettingsDirectory/Resources/Equipment').then((file) => ({
        default: file.Equipment,
      })),
  },
  {
    path: '/settings/users/:userId',
    name: 'User details',
    route: PrivateRoute,
    permissions: [USERS_MANAGE],
    importFrom: () =>
      import('./components/SettingsDirectory/Site/Users/DetailsDirectory').then((file) => ({
        default: file.UserDetails,
      })),
  },
  {
    path: '/settings/users',
    name: 'Users',
    exact: true,
    route: PrivateRoute,
    importFrom: () =>
      import('./components/SettingsDirectory/Site/Users').then((file) => ({ default: file.Users })),
  },
  {
    path: '/settings/roles',
    name: 'Roles',
    exact: true,
    route: PrivateRoute,
    importFrom: () =>
      import('./components/SettingsDirectory/System/Roles').then((file) => ({
        default: file.Roles,
      })),
  },
  {
    path: '/settings/roles/:roleId',
    name: 'Edit role settings',
    route: PrivateRoute,
    importFrom: () =>
      import('./components/SettingsDirectory/System/Roles/Edit').then((file) => ({
        default: file.EditRoleSettings,
      })),
  },
  {
    path: '/settings/organisation-security',
    name: 'Organisation security',
    route: PrivateRoute,
    importFrom: () =>
      import('./components/SettingsDirectory/System/OrganisationSecurity').then((file) => ({
        default: file.OrganisationSecurity,
      })),
  },
  {
    path: '/settings/resources/rooms',
    name: 'Rooms',
    route: PrivateRoute,
    importFrom: () =>
      import('./components/SettingsDirectory/Resources/Rooms').then((file) => ({
        default: file.Rooms,
      })),
  },
  {
    path: '/settings/resources/practitioners',
    name: 'Practitioners',
    route: PrivateRoute,
    importFrom: () =>
      import('./components/SettingsDirectory/Resources/Practitioners').then((file) => ({
        default: file.Practitioners,
      })),
  },
  {
    path: '/settings/site/categories',
    name: 'Categories',
    route: PrivateRoute,
    importFrom: () =>
      import('./components/SettingsDirectory/Site/Categories').then((file) => ({
        default: file.Categories,
      })),
  },
  {
    path: '/settings/site/voucher-delivery',
    name: 'Voucher delivery',
    route: PrivateRoute,
    exact: true,
    importFrom: () =>
      import('./components/SettingsDirectory/Site/VoucherDelivery').then((file) => ({
        default: file.VoucherDelivery,
      })),
  },
  {
    path: '/settings/site/appointment-enquiries',
    name: 'Appointment enquiries',
    route: PrivateRoute,
    exact: true,
    permissions: [SETTINGS_VIEW],
    importFrom: () =>
      import('./components/SettingsDirectory/Site/Categories').then((file) => ({
        default: file.Categories,
      })),
  },
  {
    path: '/settings/clients/membership-types/:membershipTypeId',
    name: 'Edit membership type',
    route: PrivateRoute,
    importFrom: () =>
      import('./components/SettingsDirectory/Clients/Membership/Edit').then((file) => ({
        default: file.MembershipTypeEdit,
      })),
  },
  {
    path: '/settings/clients/membership-type/:membershipTypeId/credits/:creditRuleId',
    name: 'Edit credit rule',
    route: PrivateRoute,
    exact: true,
    importFrom: () =>
      import('./components/SettingsDirectory/Clients/Membership/Edit/EditCreditRule').then(
        (file) => ({ default: file.EditCreditRule })
      ),
  },
  {
    path: '/settings/clients/membership-type/:membershipTypeId/credits/',
    name: 'Edit credit rule',
    route: PrivateRoute,
    exact: true,
    importFrom: () =>
      import('./components/SettingsDirectory/Clients/Membership/Edit/EditCreditRule').then(
        (file) => ({ default: file.EditCreditRule })
      ),
  },
  {
    path: '/settings/marketing-preferences',
    name: 'Marketing preferences',
    route: PrivateRoute,
    importFrom: () =>
      import('./components/SettingsDirectory/Marketing/MarketingPreferences').then((file) => ({
        default: file.MarketingPreferences,
      })),
  },
  {
    path: '/settings/clients/client-labels',
    name: 'Client labels',
    route: PrivateRoute,
    importFrom: () =>
      import('./components/SettingsDirectory/Clients/ClientLabels').then((file) => ({
        default: file.ClientLabels,
      })),
  },
  {
    path: '/settings/orders/emails',
    name: 'Email template settings',
    route: PrivateRoute,
    exact: true,
    importFrom: () =>
      import('./components/SettingsDirectory/Emails').then((file) => ({
        default: file.EmailsSettings,
      })),
  },
  {
    path: '/settings/orders/emails/templates/:messageFragmentId',
    name: 'Edit message template',
    route: PrivateRoute,
    exact: true,
    importFrom: () =>
      import('./components/SettingsDirectory/Emails/Edit/EditMessageFragment').then((file) => ({
        default: file.EditMessageFragment,
      })),
  },
  {
    path: '/settings/sms-notifications',
    name: 'SMS notifications',
    route: PrivateRoute,
    importFrom: () =>
      import('./components/SettingsDirectory/Marketing/SmsNotifications').then((file) => ({
        default: file.SmsNotifications,
      })),
  },
  {
    path: '/settings/sms-notifications/templates',
    name: 'SMS notifications',
    route: PrivateRoute,
    importFrom: () =>
      import('./components/SettingsDirectory/Marketing/SmsNotifications').then((file) => ({
        default: file.SmsNotifications,
      })),
  },
  {
    path: '/settings/sms-templates/:smsFragmentId',
    name: 'Edit SMS template',
    route: PrivateRoute,
    exact: true,
    importFrom: () =>
      import('./components/SettingsDirectory/Marketing/SmsNotifications/SmsTemplates/Edit').then(
        (file) => ({ default: file.EditSmsTemplate })
      ),
  },
  {
    path: '/emails/campaigns/:campaignId/details',
    name: 'Campaign details',
    route: PrivateRoute,
    importFrom: () =>
      import('./components/Emails/components/Campaign/components/Details').then((file) => ({
        default: file.Details,
      })),
  },
  {
    path: '/emails/campaigns/:campaignId/content',
    name: 'Campaign content',
    route: PrivateRoute,
    importFrom: () =>
      import('./components/Emails/components/Campaign/components/Content').then((file) => ({
        default: file.Content,
      })),
  },
  {
    path: '/emails/campaigns/:campaignId/send',
    name: 'Send campaign',
    route: PrivateRoute,
    importFrom: () =>
      import('./components/Emails/components/Campaign/components/Send').then((file) => ({
        default: file.Send,
      })),
  },
  {
    path: '/emails/campaigns/:campaignId',
    name: 'Edit campaign',
    route: PrivateRoute,
    importFrom: () =>
      import('./components/Emails/components/Campaign').then((file) => ({
        default: file.CampaignHome,
      })),
  },
  {
    path: '/emails/campaigns',
    name: 'Email campaigns',
    route: PrivateRoute,
    importFrom: () =>
      import('./components/Emails/components/CampaignsList').then((file) => ({
        default: file.CampaignsList,
      })),
  },
  {
    path: '/emails/lists/:emailListId/details',
    name: 'Email list details',
    route: PrivateRoute,
    importFrom: () =>
      import('./components/Emails/components/EmailList/components/Details').then((file) => ({
        default: file.Details,
      })),
  },
  {
    path: '/emails/lists/:emailListId/subscribers',
    name: 'Email list subscribers',
    route: PrivateRoute,
    importFrom: () =>
      import('./components/Emails/components/EmailList/components/SubscribersList').then(
        (file) => ({ default: file.SubscribersList })
      ),
  },
  {
    path: '/emails/lists/:emailListId',
    name: 'Email list details',
    route: PrivateRoute,
    importFrom: () =>
      import('./components/Emails/components/EmailList').then((file) => ({
        default: file.EmailListHome,
      })),
  },
  {
    path: '/emails/lists',
    name: 'Email lists',
    route: PrivateRoute,
    importFrom: () =>
      import('./components/Emails/components/EmailLists').then((file) => ({
        default: file.EmailLists,
      })),
  },
  {
    path: '/emails',
    name: 'Emails',
    route: PrivateRoute,
    importFrom: () =>
      import('./components/Emails/EmailsHome').then((file) => ({ default: file.EmailsHome })),
  },
  {
    path: '/debug/intake-forms-list',
    name: 'Intake forms',
    route: PrivateRoute,
    importFrom: () =>
      import('./screens/IntakeFormsSheet/IntakeFormsSheet').then((file) => ({
        default: file.IntakeFormsSheetInner,
      })),
  },
  {
    path: '/debug/availability',
    name: 'Availability details',
    route: PrivateRoute,
    importFrom: () =>
      import('./screens/AvailabilityDetails/AvailabilityDetails').then((file) => ({
        default: file.AvailabilityDetails,
      })),
  },
]

const routesToExport = routes.map((route) => {
  const { importFrom, ...rest } = route

  if (importFrom) {
    return {
      ...rest,
      component: ReactLazyPreload(importFrom),
    }
  }

  return rest
})

export { routesToExport as routes, PrivateRoute }
